import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProposalStore } from '../stores/proposalStore';
import { ProposalEditor } from '../components/proposals/ProposalEditor';
import { FileText, Users, Calendar, ArrowLeft, Save, Share2, Loader2 } from 'lucide-react';
import { samGovApi } from '../lib/api/samGov';
import { toast } from 'react-hot-toast';

export function ProposalDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [contractData, setContractData] = useState<any>(null);
  const { proposals, updateProposal } = useProposalStore();
  const proposal = proposals.find(p => p.id === id);

  useEffect(() => {
    async function fetchData() {
      if (!proposal) {
        toast.error('Proposal not found');
        navigate('/proposals');
        return;
      }

      setLoading(true);
      try {
        // Check if we're using mock data
        if (import.meta.env.VITE_USE_MOCK_DATA === 'true') {
          setContractData({
            title: 'Sample Contract',
            agency: 'Department of Defense',
            value: 1000000,
            deadline: '2024-12-31',
          });
        } else {
          // Fetch real contract data
          const response = await samGovApi.fetchContracts({ 
            noticeId: proposal.contractId,
            limit: 1
          });

          if (response && response.length > 0) {
            setContractData(response[0]);
          } else {
            throw new Error('Contract not found');
          }
        }
      } catch (error) {
        console.error('Failed to fetch contract data:', error);
        toast.error('Failed to load contract details');
        // Set fallback contract data
        setContractData({
          title: proposal.title,
          agency: 'Not available',
          value: 'N/A',
          deadline: proposal.deadline,
        });
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [proposal, navigate]);

  const handleSave = async (updatedProposal: any) => {
    try {
      await updateProposal(id!, updatedProposal);
      toast.success('Proposal updated successfully');
    } catch (error) {
      console.error('Failed to update proposal:', error);
      toast.error('Failed to update proposal');
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!proposal) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/proposals')}
            className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">{proposal.title}</h1>
            <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
              <span className="flex items-center">
                <Calendar className="mr-1.5 h-4 w-4" />
                Due {new Date(proposal.deadline).toLocaleDateString()}
              </span>
              <span className="flex items-center">
                <Users className="mr-1.5 h-4 w-4" />
                {proposal.team.length} team members
              </span>
              <span className="flex items-center">
                <FileText className="mr-1.5 h-4 w-4" />
                {proposal.sections.filter(s => s.status === 'completed').length}/{proposal.sections.length} sections complete
              </span>
            </div>
          </div>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => toast.success('Share functionality coming soon')}
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          >
            <Share2 className="mr-2 h-4 w-4" />
            Share
          </button>
          <button
            onClick={() => handleSave(proposal)}
            className="inline-flex items-center rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700"
          >
            <Save className="mr-2 h-4 w-4" />
            Save
          </button>
        </div>
      </div>

      {/* Contract Details */}
      {contractData && (
        <div className="rounded-lg bg-gray-50 p-4">
          <h2 className="text-sm font-medium text-gray-700">Contract Details</h2>
          <div className="mt-2 grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Agency</p>
              <p className="text-sm font-medium text-gray-900">{contractData.agency}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Value</p>
              <p className="text-sm font-medium text-gray-900">
                {typeof contractData.value === 'number'
                  ? new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: 'USD',
                      minimumFractionDigits: 0,
                    }).format(contractData.value)
                  : contractData.value}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Proposal Editor */}
      <ProposalEditor proposal={proposal} onSave={handleSave} />
    </div>
  );
}